import React, { useEffect, useState } from 'react';
import { Button, Col, Divider, message, Modal, Row, Select, Spin } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Link } from 'lucide-react';
import { get, post } from '../../utils/fetch';
import styles from './JiraUserMappingModal.module.css';

type OptionType = {
  value: string;
  label: string;
  email?: string;
  originalLabel?: string;
};
type Mapping = { mondayUserId: string; bugherdUserId: string };
type BugherdUser = {
  id: string | number;
  display_name?: string;
  name?: string;
  email?: string;
};

interface Props {
  open: boolean;
  onClose: () => void;
  organizationId: string | number;
  integrationId: string | null;
  projectIntegrationId: string | null;
  boardId: string | null;
  bugherdUsers: BugherdUser[];
  container?: HTMLElement | (() => HTMLElement);
}

const emptyMapping = { mondayUserId: '', bugherdUserId: '' };

const MondayUserMappingModal: React.FC<Props> = ({
  open,
  onClose,
  organizationId,
  integrationId,
  projectIntegrationId,
  boardId,
  bugherdUsers,
  container
}) => {
  const [loading, setLoading] = useState(false);
  const [mondayUsers, setMondayUsers] = useState<OptionType[]>([]);
  const [mappings, setMappings] = useState<Mapping[]>([emptyMapping]);
  const hasBoardId = !!boardId && boardId !== 'undefined';

  useEffect(() => {
    if (open && integrationId && hasBoardId && projectIntegrationId) {
      loadData();
    }

    if (open && (!integrationId || !hasBoardId || !projectIntegrationId)) {
      message.warning(
        'Please save a Monday board before managing assigned user mapping.'
      );
    }
  }, [open, integrationId, hasBoardId, boardId, projectIntegrationId]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([fetchMondayUsers(), fetchCurrentSettings()]);
    setLoading(false);
  };

  const fetchMondayUsers = async () => {
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/monday_users?id=${integrationId}`
      );
      setMondayUsers(
        results.map((user: OptionType) => ({
          ...user,
          label: user.email ? `${user.label} (${user.email})` : user.label,
          originalLabel: user.label
        }))
      );
    } catch (error) {
      message.error('Failed to fetch Monday users.');
    }
  };

  const fetchCurrentSettings = async () => {
    try {
      const result = await get(
        `/organizations/${organizationId}/integrations/${integrationId}/project_integrations/${projectIntegrationId}`
      );
      const initialMappings = Object.entries(
        result?.settings?.user_mapping || {}
      ).map(([mondayUserId, bugherdUserId]) => ({
        mondayUserId,
        bugherdUserId: String(bugherdUserId)
      }));
      setMappings(initialMappings.length > 0 ? initialMappings : [emptyMapping]);
    } catch (error) {
      setMappings([emptyMapping]);
    }
  };

  const saveMappings = async (currentMappings: Mapping[]) => {
    try {
      const userMappingPayload = currentMappings.reduce((acc, mapping) => {
        if (mapping.mondayUserId && mapping.bugherdUserId) {
          acc[mapping.mondayUserId] = mapping.bugherdUserId;
        }
        return acc;
      }, {} as Record<string, string>);

      await post(
        `/organizations/${organizationId}/integrations/${integrationId}/project_integrations/save_settings?integration_code=Monday`,
        {
          project_integration_id: projectIntegrationId,
          settings: {
            user_mapping: userMappingPayload
          }
        }
      );
    } catch (error) {
      message.error('Failed to save user mappings.');
    }
  };

  const handleAddMapping = () => {
    setMappings([...mappings, emptyMapping]);
  };

  const handleRemoveMapping = (index: number) => {
    const newMappings = mappings.filter((_, i) => i !== index);
    setMappings(newMappings);
    saveMappings(newMappings);
  };

  const handleMappingChange = (
    index: number,
    field: 'mondayUserId' | 'bugherdUserId',
    value: string
  ) => {
    const newMappings = [...mappings];
    newMappings[index][field] = value;
    setMappings(newMappings);

    if (newMappings[index].mondayUserId && newMappings[index].bugherdUserId) {
      saveMappings(newMappings);
    }
  };

  const handleAutoMap = () => {
    const autoPairs: Mapping[] = [];

    mondayUsers.forEach(mondayUser => {
      const remoteLabel = (
        mondayUser.originalLabel || mondayUser.label
      ).toLowerCase();
      const matchedBugherdUser = bugherdUsers.find(
        user =>
          (mondayUser.email &&
            user.email?.toLowerCase() === mondayUser.email.toLowerCase()) ||
          user.display_name?.toLowerCase() === remoteLabel ||
          user.name?.toLowerCase() === remoteLabel ||
          user.email?.toLowerCase() === remoteLabel
      );

      if (matchedBugherdUser) {
        autoPairs.push({
          mondayUserId: mondayUser.value,
          bugherdUserId: String(matchedBugherdUser.id)
        });
      }
    });

    if (autoPairs.length > 0) {
      setMappings(autoPairs);
      saveMappings(autoPairs);
      message.success(`Successfully mapped ${autoPairs.length} users.`);
    } else {
      message.info('No matching users found by name or email.');
    }
  };

  const bugherdUserOptions = bugherdUsers.map(user => ({
    label: user.display_name || user.name || user.email,
    value: String(user.id)
  }));

  const renderMappingList = () => (
    <div className={styles.mappingList}>
      <div className={styles.mappingRowsContainer}>
        <Row gutter={16} className={styles.headerRow}>
          <Col span={10}>
            <strong>BugHerd User</strong>
          </Col>
          <Col span={2}></Col>
          <Col span={10}>
            <strong>Monday User</strong>
          </Col>
          <Col span={2}></Col>
        </Row>
        {mappings.map((mapping, index) => (
          <Row
            key={index}
            gutter={16}
            className={styles.mappingRow}
            align="middle"
          >
            <Col span={10}>
              <Select
                showSearch
                style={{ width: '100%' }}
                placeholder="Search BugHerd user"
                optionFilterProp="label"
                value={mapping.bugherdUserId || undefined}
                onChange={value =>
                  handleMappingChange(index, 'bugherdUserId', value)
                }
                options={bugherdUserOptions}
              />
            </Col>
            <Col span={2} style={{ textAlign: 'center' }}>
              <Link size={16} className={styles.linkIcon} />
            </Col>
            <Col span={10}>
              <Select
                showSearch
                style={{ width: '100%' }}
                placeholder="Search Monday user"
                optionFilterProp="label"
                value={mapping.mondayUserId || undefined}
                onChange={value =>
                  handleMappingChange(index, 'mondayUserId', value)
                }
                options={mondayUsers}
              />
            </Col>
            <Col span={2}>
              <Button
                type="text"
                icon={<MinusCircleOutlined />}
                onClick={() => handleRemoveMapping(index)}
                danger
              />
            </Col>
          </Row>
        ))}
        <div className={styles.addMapping}>
          <Button type="link" onClick={handleAddMapping} icon={<PlusOutlined />}>
            Sync another user
          </Button>
        </div>
      </div>
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <Divider plain>or</Divider>
        <Button
          type="primary"
          size="large"
          onClick={handleAutoMap}
          style={{ padding: '0 40px' }}
        >
          Automap BugHerd to Monday users
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      title="Manage assigned users between BugHerd and Monday"
      open={open}
      onCancel={onClose}
      width={700}
      footer={null}
      getContainer={container}
    >
      <div className={styles.modalContent}>
        <p>
          Match BugHerd assigned users to Monday users so synced tasks are
          assigned correctly in Monday.
        </p>
        {loading ? (
          <div className={styles.loader}>
            <Spin />
          </div>
        ) : (
          renderMappingList()
        )}
      </div>
    </Modal>
  );
};

export default MondayUserMappingModal;
